import { Box, Typography, styled } from '@mui/material';
import { vec3 } from 'gl-matrix';
import {
    KeyboardEvent,
    PointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoHint from '~/components/Inputs/InfoHint';
import { formatPetDisplayValue } from '~/domain/dicom/intensityUnits';
import type { Image3D } from '~/domain/patient/image3d';
import {
    RotatedMip,
    rotatedMipPointToPx,
    rotatedMipRayMaximumAt,
} from '~/domain/services/rotatedMip';
import { windowedGreyscaleRgba } from '~/domain/services/windowedGreyscale';
import type { Environment } from '~/engine/data/Environment';
import { useRotatedMip } from '~/hooks/image/useRotatedMip';
import {
    CLICK_SLOP_PX,
    isWithinClickSlop,
} from '~/containers/viewer/viewport/clickSlop';
import { directionLabel } from '~/utils/orientation';
import {
    EDGE_LETTER_INSET_PX,
    EdgeLetters,
    WIDGET_RESIZE_HANDLE_PX,
} from '~/viewportGrid';
import { rowPointInPetFrame } from '~/viewportGrid/adapters/engine';

const ROTATE_DEG_PER_PX = 0.5;
const KEY_ROTATE_STEP_DEG = 5;
const CROSSHAIR_STYLE = 'rgba(57, 255, 136, 0.95)';
const CROSSHAIR_ARM_PX = 7;
const CROSSHAIR_GAP_PX = 2;
const TEXT_MAX_WIDTH = `calc(100% - ${2 * EDGE_LETTER_INSET_PX}px)`;
const SUPERIOR = vec3.fromValues(0, 0, 1);
const TOP_EDGE_LABEL = directionLabel(SUPERIOR);
const BOTTOM_EDGE_LABEL = directionLabel(vec3.negate(vec3.create(), SUPERIOR));

const OverlayText = styled(Typography)(({ theme }) => ({
    fontSize: 11,
    lineHeight: 1.3,
    color: theme.palette.common.white,
    textShadow: '0 0 3px #000, 0 0 2px #000',
    pointerEvents: 'none',
    userSelect: 'none',
}));

/** Azimuth folded into [0, 360), for display and snap comparison. */
export function normalizedAzimuth(azimuthDeg: number): number {
    return ((azimuthDeg % 360) + 360) % 360;
}

/** Where the projection is drawn inside the pane, in CSS px. */
interface DrawRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

/** The largest rect of the projection's physical aspect that fits. */
function fitProjection(
    mip: RotatedMip,
    width: number,
    height: number
): DrawRect | null {
    const { frame } = mip;
    const aspect =
        (frame.heightPx * frame.pixelHeightMm) /
        (frame.widthPx * frame.pixelWidthMm);
    if (!(width > 0 && height > 0 && aspect > 0)) {
        return null;
    }
    const drawWidth = Math.min(width, height / aspect);
    const drawHeight = drawWidth * aspect;
    return {
        left: (width - drawWidth) / 2,
        top: (height - drawHeight) / 2,
        width: drawWidth,
        height: drawHeight,
    };
}

/** Pixel ranges (half-open) bounding the volume; loose for a tilted grid. */
function volumeExtentPx(mip: RotatedMip): {
    firstColumn: number;
    endColumn: number;
    firstRow: number;
    endRow: number;
} {
    const [sizeX, sizeY, sizeZ] = mip.frame.sizeVoxels;
    let minColumn = Infinity;
    let maxColumn = -Infinity;
    let minRow = Infinity;
    let maxRow = -Infinity;
    for (const x of [0.5, sizeX - 0.5]) {
        for (const y of [0.5, sizeY - 0.5]) {
            for (const z of [0.5, sizeZ - 0.5]) {
                const [column, row] = rotatedMipPointToPx(mip, [x, y, z]);
                minColumn = Math.min(minColumn, column);
                maxColumn = Math.max(maxColumn, column);
                minRow = Math.min(minRow, row);
                maxRow = Math.max(maxRow, row);
            }
        }
    }
    const { widthPx, heightPx } = mip.frame;
    return {
        firstColumn: Math.max(0, Math.floor(minColumn)),
        endColumn: Math.min(widthPx, Math.floor(maxColumn) + 1),
        firstRow: Math.max(0, Math.floor(minRow)),
        endRow: Math.min(heightPx, Math.floor(maxRow) + 1),
    };
}

/** A pane-relative CSS position as continuous projection pixel coordinates. */
function toProjectionPx(
    mip: RotatedMip,
    rect: DrawRect,
    position: readonly [number, number]
): [number, number] {
    return [
        ((position[0] - rect.left) / rect.width) * mip.frame.widthPx,
        ((position[1] - rect.top) / rect.height) * mip.frame.heightPx,
    ];
}

interface PetMipPaneProps {
    readonly petImage: Image3D;
    /** The PET row's Environment, whose crosshair is marked. */
    readonly environment: Environment | undefined;
    /** CSS px; the projection letterboxes inside. */
    readonly widthPx: number;
    readonly heightPx: number;
    /** Degrees about the patient's superior axis. */
    readonly azimuthDeg: number;
    readonly onTurn: (deltaDeg: number) => void;
    readonly isInverted: boolean;
    /** Called with the voxel index the readout named. Absent: rotate-only. */
    readonly onLocate?: (voxelIndex: number) => void;
}

/**
 * A PET maximum-intensity projection the user turns about the patient's
 * superior axis. The readout is one stored voxel, not a measurement, and a
 * click hands `onLocate` that same voxel rather than a re-search.
 */
export default function PetMipPane({
    petImage,
    environment,
    widthPx,
    heightPx,
    azimuthDeg,
    onTurn,
    isInverted,
    onLocate,
}: PetMipPaneProps) {
    const intl = useIntl();
    const result = useRotatedMip(petImage, azimuthDeg);
    const mip = result?.state === 'ready' ? result.mip : null;
    const autoWindow = result?.state === 'ready' ? result.autoWindow : null;

    // Canvases in state, so a remount re-runs the paint effects.
    const [imageCanvas, setImageCanvas] = useState<HTMLCanvasElement | null>(
        null
    );
    const [overlayCanvas, setOverlayCanvas] =
        useState<HTMLCanvasElement | null>(null);

    const drawRect = useMemo(
        () => (mip ? fitProjection(mip, widthPx, heightPx) : null),
        [mip, widthPx, heightPx]
    );

    // Keep the position, not the hit, so a turn re-derives the readout.
    const [hoverPosition, setHoverPosition] = useState<[number, number] | null>(
        null
    );
    const hoverHit =
        mip && drawRect && hoverPosition
            ? rotatedMipRayMaximumAt(
                  mip,
                  toProjectionPx(mip, drawRect, hoverPosition)
              )
            : null;

    const sourceCanvas = useMemo(() => {
        if (!mip || !autoWindow) {
            return null;
        }
        const { widthPx: sourceWidth, heightPx: sourceHeight } = mip.frame;
        const source = document.createElement('canvas');
        source.width = sourceWidth;
        source.height = sourceHeight;
        const context = source.getContext('2d');
        if (!context) {
            return null;
        }
        context.putImageData(
            new ImageData(
                windowedGreyscaleRgba(
                    mip.rayMaximum,
                    autoWindow[0],
                    autoWindow[1],
                    isInverted
                ),
                sourceWidth,
                sourceHeight
            ),
            0,
            0
        );
        return source;
    }, [mip, autoWindow, isInverted]);

    useEffect(() => {
        const canvas = imageCanvas;
        const context = canvas?.getContext('2d');
        if (!canvas || !context) {
            return;
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (!mip || !sourceCanvas || !drawRect) {
            return;
        }
        if (mip.coverage === 'footprint') {
            // A footprint leaves the in-volume background undrawn: fill it with
            // the window's lower end. Beyond the volume stays unfilled, so no
            // data must not read as absence of uptake.
            const extent = volumeExtentPx(mip);
            const scaleX = drawRect.width / mip.frame.widthPx;
            const scaleY = drawRect.height / mip.frame.heightPx;
            context.fillStyle = isInverted ? '#fff' : '#000';
            context.fillRect(
                drawRect.left + extent.firstColumn * scaleX,
                drawRect.top + extent.firstRow * scaleY,
                (extent.endColumn - extent.firstColumn) * scaleX,
                (extent.endRow - extent.firstRow) * scaleY
            );
        }
        // One screen block per projection pixel, the unit a click resolves to.
        context.imageSmoothingEnabled = false;
        context.drawImage(
            sourceCanvas,
            drawRect.left,
            drawRect.top,
            drawRect.width,
            drawRect.height
        );
    }, [
        imageCanvas,
        mip,
        sourceCanvas,
        drawRect,
        isInverted,
        widthPx,
        heightPx,
    ]);

    const drawCrosshair = useCallback((): void => {
        const canvas = overlayCanvas;
        const context = canvas?.getContext('2d');
        if (!canvas || !context) {
            return;
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (!mip || !drawRect || !environment) {
            return;
        }
        // The point is in the row's frame; the projection is of the PET's.
        const point = rowPointInPetFrame(
            environment,
            environment.planeIntersectionPoint.coordinates
        );
        if (!point || !petImage.grid.includesPoint(point)) {
            return;
        }
        const [column, row] = rotatedMipPointToPx(
            mip,
            petImage.grid.toVoxelSpace(point)
        );
        // Beside the patient, outside the cropped frame: nothing to mark.
        if (
            !(
                column >= 0 &&
                column <= mip.frame.widthPx &&
                row >= 0 &&
                row <= mip.frame.heightPx
            )
        ) {
            return;
        }
        const x = drawRect.left + (column / mip.frame.widthPx) * drawRect.width;
        const y = drawRect.top + (row / mip.frame.heightPx) * drawRect.height;
        context.strokeStyle = CROSSHAIR_STYLE;
        context.lineWidth = 1.5;
        context.beginPath();
        for (const [dx, dy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
        ]) {
            context.moveTo(
                x + dx * CROSSHAIR_GAP_PX,
                y + dy * CROSSHAIR_GAP_PX
            );
            context.lineTo(
                x + dx * CROSSHAIR_ARM_PX,
                y + dy * CROSSHAIR_ARM_PX
            );
        }
        context.stroke();
    }, [overlayCanvas, petImage, environment, mip, drawRect]);

    // Resizing a canvas clears it, so the pane size is a dependency.
    useEffect(() => {
        drawCrosshair();
    }, [drawCrosshair, widthPx, heightPx]);

    useEffect(() => {
        if (!environment) {
            return;
        }
        const point = environment.planeIntersectionPoint;
        const observer = () => drawCrosshair();
        point.subscribe(observer);
        return () => point.unsubscribe(observer);
    }, [environment, drawCrosshair]);

    const locate = (position: readonly [number, number]): void => {
        if (!mip || !drawRect || !onLocate) {
            return;
        }
        const hit = rotatedMipRayMaximumAt(
            mip,
            toProjectionPx(mip, drawRect, position)
        );
        if (hit) {
            onLocate(hit.voxelIndex);
        }
    };

    const dragRef = useRef<{
        startX: number;
        startY: number;
        lastX: number;
        isRotating: boolean;
    } | null>(null);

    const panePosition = (
        event: PointerEvent<HTMLElement>
    ): [number, number] => {
        const bounds = event.currentTarget.getBoundingClientRect();
        return [event.clientX - bounds.left, event.clientY - bounds.top];
    };

    const handlePointerDown = (event: PointerEvent<HTMLElement>): void => {
        if (event.button !== 0) {
            return;
        }
        dragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            lastX: event.clientX,
            isRotating: false,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: PointerEvent<HTMLElement>): void => {
        const drag = dragRef.current;
        if (drag) {
            // Without the slop a tremor during a click would turn the view,
            // and the click would locate a voxel on another ray.
            if (
                !drag.isRotating &&
                Math.abs(event.clientX - drag.startX) >= CLICK_SLOP_PX
            ) {
                drag.isRotating = true;
                drag.lastX = drag.startX;
            }
            if (drag.isRotating) {
                const dx = event.clientX - drag.lastX;
                drag.lastX = event.clientX;
                // Dragging right brings the patient's right side into view.
                onTurn(-dx * ROTATE_DEG_PER_PX);
                setHoverPosition(null);
                return;
            }
        }
        setHoverPosition(panePosition(event));
    };

    const endDrag = (event: PointerEvent<HTMLElement>): void => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        dragRef.current = null;
    };

    const handlePointerUp = (event: PointerEvent<HTMLElement>): void => {
        const drag = dragRef.current;
        endDrag(event);
        if (
            drag &&
            !drag.isRotating &&
            isWithinClickSlop(
                [drag.startX, drag.startY],
                [event.clientX, event.clientY]
            )
        ) {
            locate(panePosition(event));
        }
        setHoverPosition(panePosition(event));
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            onTurn((event.key === 'ArrowRight' ? -1 : 1) * KEY_ROTATE_STEP_DEG);
        }
    };

    const radians = (azimuthDeg * Math.PI) / 180;
    const viewerRight = vec3.fromValues(
        Math.cos(radians),
        Math.sin(radians),
        0
    );
    const leftEdgeLabel = directionLabel(
        vec3.negate(vec3.create(), viewerRight)
    );
    const rightEdgeLabel = directionLabel(viewerRight);

    return (
        <Box
            sx={{
                position: 'relative',
                width: widthPx,
                height: heightPx,
                overflow: 'hidden',
                cursor: mip && onLocate ? 'crosshair' : 'default',
                touchAction: 'none',
                '&:focus-visible': {
                    outline: (theme) =>
                        `1px solid ${theme.palette.primary.main}`,
                    outlineOffset: -1,
                },
            }}
            tabIndex={0}
            role="img"
            aria-label={intl.formatMessage({
                id: onLocate
                    ? 'petMip.ariaLabel'
                    : 'petMip.ariaLabelRotateOnly',
            })}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={endDrag}
            onPointerLeave={() => setHoverPosition(null)}
            onKeyDown={handleKeyDown}
            data-testid="pet-mip-projection"
        >
            <canvas
                ref={setImageCanvas}
                width={widthPx}
                height={heightPx}
                style={{ position: 'absolute', inset: 0 }}
            />
            <canvas
                ref={setOverlayCanvas}
                width={widthPx}
                height={heightPx}
                style={{ position: 'absolute', inset: 0 }}
                aria-hidden
            />
            {mip && drawRect && (
                <>
                    <EdgeLetters
                        top={TOP_EDGE_LABEL}
                        bottom={BOTTOM_EDGE_LABEL}
                        left={leftEdgeLabel}
                        right={rightEdgeLabel}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            left: EDGE_LETTER_INSET_PX,
                            top: EDGE_LETTER_INSET_PX,
                            display: 'flex',
                            fontSize: 16,
                            color: 'common.white',
                            // Readable over either greyscale polarity.
                            filter: 'drop-shadow(0 0 2px #000)',
                            '& .MuiSvgIcon-root': { ml: 0 },
                        }}
                    >
                        <InfoHint
                            title={intl.formatMessage({
                                id: onLocate
                                    ? 'petMip.hint'
                                    : 'petMip.hintRotateOnly',
                            })}
                            placement="bottom-start"
                        />
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: EDGE_LETTER_INSET_PX,
                            bottom: WIDGET_RESIZE_HANDLE_PX,
                            maxWidth: TEXT_MAX_WIDTH,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.25,
                            pointerEvents: 'none',
                        }}
                    >
                        {hoverHit ? (
                            // Wraps the label, never the value with its unit.
                            <OverlayText
                                data-testid="pet-mip-readout"
                                sx={{ maxWidth: '100%', textAlign: 'right' }}
                            >
                                <FormattedMessage
                                    id="petMip.rayMaximum"
                                    values={{
                                        value: (
                                            <Box
                                                component="span"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {formatPetDisplayValue(
                                                    hoverHit.value,
                                                    petImage.suvQuantification ??
                                                        null
                                                )}
                                            </Box>
                                        ),
                                    }}
                                />
                            </OverlayText>
                        ) : null}
                        <OverlayText data-testid="pet-mip-azimuth">
                            {`${normalizedAzimuth(azimuthDeg).toFixed(0)}°`}
                        </OverlayText>
                    </Box>
                </>
            )}
            {result && result.state !== 'ready' && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {result.state === 'waitingForSlices' ? (
                            <FormattedMessage
                                id="petMip.waitingForSlices"
                                values={{
                                    loaded: result.loaded,
                                    total: result.total,
                                }}
                            />
                        ) : result.state === 'imageIncomplete' ? (
                            <FormattedMessage
                                id="petMip.imageIncomplete"
                                values={{
                                    unavailable: result.unavailable,
                                    total: result.total,
                                }}
                            />
                        ) : (
                            <FormattedMessage id="petMip.unavailable" />
                        )}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
